import { useEffect } from 'react';
import * as Sentry from '@sentry/react';

import { notNil } from '@/lib/utils';
import { useAuth } from '@/auth/auth-context';
import { useWorkspaceQuery } from '@/generated/graphql';
import { parseGraphQLResponse } from '@/graphql/utils';
import { FullPageLoader } from '@/components/loader';

import { accountSchema } from './types';
import { WorkspaceContext } from './workspace-context';

interface WorkspaceContextProviderProps {
  children: React.ReactNode;
}

export const WorkspaceContextProvider = ({ children }: WorkspaceContextProviderProps) => {
  const { user } = useAuth();

  const { data, loading } = useWorkspaceQuery({
    fetchPolicy: 'cache-and-network',
    skip: user === null || !user.emailVerified,
  });

  const userId = data?.me?.userId ?? undefined;
  const userEmail = data?.me?.email ?? user?.email;

  useEffect(() => {
    if (userId === undefined && userEmail === undefined) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({ id: userId, email: userEmail });
  }, [userId, userEmail]);

  if (data === undefined && loading && location.pathname !== '/signup') {
    return <FullPageLoader />;
  }

  const workspaceData = {
    accounts: (data?.accounts ?? []).map((input) => parseGraphQLResponse(input, accountSchema)),
    user: notNil(data?.me)
      ? {
          userId: data.me.userId,
          email: data.me.email,
          emailVerified: user?.emailVerified ?? false,
        }
      : user,
  };

  return <WorkspaceContext.Provider value={workspaceData}>{children}</WorkspaceContext.Provider>;
};
